import { MapPin, Search } from "lucide-react";
import React, { useEffect, useRef, useState } from "react";
import {
  Breadcrumbs,
  Button,
  Group,
  LoadingOverlay,
  NumberInput,
  Paper,
  Select,
  Stack,
  Tabs,
  Text,
  TextInput,
  createStyles,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { showNotification } from "@mantine/notifications";
import { useNavigate, useParams } from "react-router-dom";
import {
  APIProvider,
  Map,
  AdvancedMarker,
  useMapsLibrary,
} from "@vis.gl/react-google-maps";
import PageContainer from "../../../themes/components/PageContainer";
import PageHeading from "../../../themes/components/PageHeading";
import { useBreadcrumbStyles } from "../styles/sharedStyles";
import ClientBadge from "../elements/ClientBadge";
import MapCenterSync from "../components/MapCenterSync";
import GeofenceCircle from "../components/GeofenceCircle";
import {
  useGetShiftLocationApiService,
  useUpdateShiftLocationApiService,
} from "../services/api/SiteConfigApiService";
import {
  useListShiftSites,
  useCreateShiftSite,
  useListShiftSiteGroups,
  useCreateShiftSiteGroup,
  useAssignLocationToShiftSite,
  useUpdateShiftSite,
} from "../services/api/ShiftSiteGroupsCrudApiService";
import AccessGuard from "../../../common/uac/AccessGuard";

const googleMapsApiKey = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "";

const DEFAULT_CENTER = { lat: -33.9, lng: 18.4 };
const DEFAULT_ZOOM = 14;
const FORM_MAX_WIDTH = 624;

const LATE_ARRIVAL_OPTIONS = [
  { value: "0", label: "0 (no grace)" },
  { value: "5", label: "5" },
  { value: "10", label: "10" },
  { value: "15", label: "15" },
  { value: "20", label: "20" },
  { value: "25", label: "25" },
  { value: "30", label: "30" },
  { value: "45", label: "45" },
  { value: "60", label: "60" },
];

const useStyles = createStyles(() => ({
  separator: {
    height: 1,
    background:
      "linear-gradient(90deg, rgba(0,0,0,0.07) 0%, rgba(0,0,0,0.07) 100%), linear-gradient(90deg, #f2f2f2 0%, #f2f2f2 100%)",
  },
}));

// ── Site & Grouping Tab ─────────────────────────────────────────
const ShiftSiteGroupingTab: React.FC<{ locationId: string }> = ({
  locationId,
}) => {
  const { data: sitesData, refetch: refetchSites } = useListShiftSites();
  const { data: groupsData, refetch: refetchGroups } = useListShiftSiteGroups();
  const createSiteMutation = useCreateShiftSite();
  const createGroupMutation = useCreateShiftSiteGroup();
  const assignLocationMutation = useAssignLocationToShiftSite();
  const updateSiteMutation = useUpdateShiftSite();

  const sites = sitesData?.results || [];
  const groups = groupsData?.results || [];

  const [selectedSiteId, setSelectedSiteId] = useState<string | null>(null);
  const [selectedGroupId, setSelectedGroupId] = useState<string | null>(null);
  const [newSiteName, setNewSiteName] = useState("");
  const [newGroupName, setNewGroupName] = useState("");
  const [showCreateSite, setShowCreateSite] = useState(false);
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  // Find current assignment from the location's site field
  const { data: locationData } = useGetShiftLocationApiService({
    id: locationId,
  });
  const currentSiteId = locationData?.site || null;

  useEffect(() => {
    if (currentSiteId) {
      setSelectedSiteId(currentSiteId);
      const site = sites.find((s) => s.id === currentSiteId);
      if (site?.site_group) {
        setSelectedGroupId(site.site_group);
      } else {
        setSelectedGroupId(null);
      }
    }
  }, [currentSiteId, sites]);

  const handleAssignSite = async (siteId: string | null): Promise<void> => {
    setSelectedSiteId(siteId);
    try {
      await assignLocationMutation.mutateAsync({
        locationId,
        site: siteId,
      });
      showNotification({
        title: "Success",
        message: siteId
          ? "Location assigned to site"
          : "Location unassigned from site",
        color: "green",
      });
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to update site assignment",
        color: "red",
      });
    }
  };

  const handleCreateSite = async (): Promise<void> => {
    if (!newSiteName.trim()) return;
    try {
      const site = await createSiteMutation.mutateAsync({
        name: newSiteName.trim(),
      });
      await refetchSites();
      setNewSiteName("");
      setShowCreateSite(false);
      await handleAssignSite(site.id);
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to create site",
        color: "red",
      });
    }
  };

  const handleAssignGroup = async (groupId: string | null): Promise<void> => {
    if (!selectedSiteId) return;
    setSelectedGroupId(groupId);
    try {
      await updateSiteMutation.mutateAsync({
        id: selectedSiteId,
        site_group: groupId,
      });
      showNotification({
        title: "Success",
        message: groupId
          ? "Site assigned to group"
          : "Site unassigned from group",
        color: "green",
      });
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to update site group",
        color: "red",
      });
    }
  };

  const handleCreateGroup = async (): Promise<void> => {
    if (!newGroupName.trim()) return;
    try {
      const group = await createGroupMutation.mutateAsync({
        name: newGroupName.trim(),
      });
      await refetchGroups();
      setNewGroupName("");
      setShowCreateGroup(false);
      await handleAssignGroup(group.id);
    } catch {
      showNotification({
        title: "Error",
        message: "Failed to create site group",
        color: "red",
      });
    }
  };

  const siteOptions = [
    { value: "", label: "None (ungrouped)" },
    ...sites.map((s) => ({ value: s.id, label: s.name })),
    { value: "__create__", label: "+ Create new site..." },
  ];

  const groupOptions = [
    { value: "", label: "None (ungrouped)" },
    ...groups.map((g) => ({ value: g.id, label: g.name })),
    { value: "__create__", label: "+ Create new group..." },
  ];

  return (
    <div className="pt-6">
      <Stack spacing="lg" style={{ maxWidth: FORM_MAX_WIDTH }}>
        <Text size="sm" color="dimmed">
          Assign this shift location to a Site and optionally group the Site
          under a regional Site Group.
        </Text>

        {/* Site assignment */}
        <div>
          <Text weight={500} size="sm" className="mb-1">
            Assign to Site
          </Text>
          <Select
            data={siteOptions}
            value={selectedSiteId || ""}
            onChange={(val) => {
              if (val === "__create__") {
                setShowCreateSite(true);
              } else {
                handleAssignSite(val || null);
              }
            }}
            placeholder="Select a site"
            size="sm"
            sx={{ maxWidth: 300 }}
          />
          {showCreateSite && (
            <Group spacing="xs" className="mt-2">
              <TextInput
                placeholder="New site name"
                value={newSiteName}
                onChange={(e) => setNewSiteName(e.currentTarget.value)}
                size="sm"
                sx={{ flex: 1, maxWidth: 220 }}
              />
              <Button
                size="sm"
                color="dark"
                onClick={handleCreateSite}
                loading={createSiteMutation.isLoading}
              >
                Create
              </Button>
              <Button
                size="sm"
                variant="subtle"
                onClick={() => setShowCreateSite(false)}
              >
                Cancel
              </Button>
            </Group>
          )}
        </div>

        {/* Site Group assignment (only shown when a site is selected) */}
        {selectedSiteId && (
          <div>
            <Text weight={500} size="sm" className="mb-1">
              Site Group (region)
            </Text>
            <Select
              data={groupOptions}
              value={selectedGroupId || ""}
              onChange={(val) => {
                if (val === "__create__") {
                  setShowCreateGroup(true);
                } else {
                  handleAssignGroup(val || null);
                }
              }}
              placeholder="Select a site group"
              size="sm"
              sx={{ maxWidth: 300 }}
            />
            {showCreateGroup && (
              <Group spacing="xs" className="mt-2">
                <TextInput
                  placeholder="New group name (e.g., Cape Town)"
                  value={newGroupName}
                  onChange={(e) => setNewGroupName(e.currentTarget.value)}
                  size="sm"
                  sx={{ flex: 1, maxWidth: 220 }}
                />
                <Button
                  size="sm"
                  color="dark"
                  onClick={handleCreateGroup}
                  loading={createGroupMutation.isLoading}
                >
                  Create
                </Button>
                <Button
                  size="sm"
                  variant="subtle"
                  onClick={() => setShowCreateGroup(false)}
                >
                  Cancel
                </Button>
              </Group>
            )}
          </div>
        )}
      </Stack>
    </div>
  );
};

interface FormValues {
  latitude: string;
  longitude: string;
  radius_allowance: string;
  late_arrival_threshold_minutes: string;
}

/** Address search using Places API (New) with useMapsLibrary to ensure lib is loaded. */
const AddressSearch: React.FC<{
  onSelect: (latVal: number, lngVal: number) => void;
}> = ({ onSelect }) => {
  const placesLib = useMapsLibrary("places");
  const [query, setQuery] = useState("");
  const [items, setItems] = useState<
    { placeId: string; description: string }[]
  >([]);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clear debounce timer on unmount
  useEffect(
    () => (): void => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    },
    []
  );

  const fetchSuggestions = async (input: string): Promise<void> => {
    if (!placesLib) return;
    try {
      const { suggestions } =
        await placesLib.AutocompleteSuggestion.fetchAutocompleteSuggestions({
          input,
        });
      setItems(
        suggestions.map((s) => ({
          placeId: s.placePrediction?.placeId || "",
          description: s.placePrediction?.text?.text || "",
        }))
      );
    } catch {
      setItems([]);
    }
  };

  const handleChange = (value: string): void => {
    setQuery(value);
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!value.trim() || !placesLib) {
      setItems([]);
      return;
    }

    debounceRef.current = setTimeout(() => {
      fetchSuggestions(value);
    }, 300);
  };

  const handleSelect = async (
    placeId: string,
    description: string
  ): Promise<void> => {
    setQuery(description);
    setItems([]);
    if (!placesLib) return;
    try {
      const place = new placesLib.Place({ id: placeId });
      await place.fetchFields({ fields: ["location"] });
      if (place.location) {
        onSelect(place.location.lat(), place.location.lng());
      }
    } catch {
      showNotification({
        title: "Error",
        message: "Could not retrieve place details. Please try again.",
        color: "red",
      });
    }
  };

  return (
    <div style={{ position: "relative" }}>
      <TextInput
        label={
          <Text size="sm" weight={500}>
            Address{" "}
            <Text component="span" color="red" size="sm">
              *
            </Text>
          </Text>
        }
        placeholder="Search"
        icon={<Search size={14} />}
        value={query}
        onChange={(e) => handleChange(e.currentTarget.value)}
        size="sm"
      />
      {items.length > 0 && (
        <Paper
          shadow="md"
          radius="sm"
          style={{
            position: "absolute",
            top: "100%",
            left: 0,
            right: 0,
            zIndex: 100,
            maxHeight: 200,
            overflow: "auto",
          }}
        >
          {items.map((item) => (
            <Text
              key={item.placeId}
              size="sm"
              p="xs"
              role="option"
              tabIndex={0}
              style={{ cursor: "pointer" }}
              onClick={() => handleSelect(item.placeId, item.description)}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleSelect(item.placeId, item.description);
                }
              }}
              sx={{ "&:hover, &:focus": { backgroundColor: "#f1f3f5" } }}
            >
              {item.description}
            </Text>
          ))}
        </Paper>
      )}
    </div>
  );
};

const ShiftSiteConfigurationPageInner: React.FC = () => {
  const { classes: bcClasses } = useBreadcrumbStyles();
  const { classes } = useStyles();
  const navigate = useNavigate();
  const { locationId } = useParams<{ locationId: string }>();

  const [activeTab, setActiveTab] = useState(0);

  const { data: location, isLoading } = useGetShiftLocationApiService({
    id: locationId || "",
    enabled: Boolean(locationId),
  });

  const locationBackUrl = location?.site
    ? `/time-attendance/shift-site/${location.site}`
    : `/time-attendance/${locationId}`;

  const { mutate: updateLocation, isLoading: isSubmitting } =
    useUpdateShiftLocationApiService();

  const form = useForm<FormValues>({
    initialValues: {
      latitude: "",
      longitude: "",
      radius_allowance: "",
      late_arrival_threshold_minutes: "",
    },
  });

  useEffect(() => {
    if (location) {
      form.setValues({
        latitude: String(location.latitude ?? ""),
        longitude: String(location.longitude ?? ""),
        radius_allowance: String(location.radius_allowance ?? ""),
        late_arrival_threshold_minutes:
          location.late_arrival_threshold_minutes != null
            ? String(location.late_arrival_threshold_minutes)
            : "",
      });
    }
  }, [location]); // eslint-disable-line

  const latRaw = parseFloat(form.values.latitude);
  const lngRaw = parseFloat(form.values.longitude);
  const hasCoords = Number.isFinite(latRaw) && Number.isFinite(lngRaw);
  const lat = hasCoords ? latRaw : 0;
  const lng = hasCoords ? lngRaw : 0;
  const mapCenter = hasCoords ? { lat, lng } : DEFAULT_CENTER;
  const radiusMeters = parseFloat(form.values.radius_allowance) || 0;

  const handleAddressSelect = (latVal: number, lngVal: number): void => {
    form.setFieldValue("latitude", String(latVal));
    form.setFieldValue("longitude", String(lngVal));
  };

  const handleMarkerDrag = (e: google.maps.MapMouseEvent): void => {
    if (e.latLng) {
      form.setFieldValue("latitude", String(e.latLng.lat()));
      form.setFieldValue("longitude", String(e.latLng.lng()));
    }
  };

  const handleUseCurrentLocation = (): void => {
    if (!navigator.geolocation) {
      showNotification({
        title: "Unavailable",
        message: "Geolocation is not supported by your browser.",
        color: "yellow",
      });
      return;
    }
    navigator.geolocation.getCurrentPosition(
      (position) => {
        form.setFieldValue("latitude", String(position.coords.latitude));
        form.setFieldValue("longitude", String(position.coords.longitude));
      },
      () => {
        showNotification({
          title: "Error",
          message: "Could not retrieve your current location.",
          color: "red",
        });
      }
    );
  };

  const handleSave = (): void => {
    if (!locationId) return;

    const parsedLat = parseFloat(parseFloat(form.values.latitude).toFixed(6));
    const parsedLng = parseFloat(parseFloat(form.values.longitude).toFixed(6));
    const parsedRadius = parseFloat(
      parseFloat(form.values.radius_allowance).toFixed(2)
    );
    const thresholdVal = form.values.late_arrival_threshold_minutes;
    const parsedThreshold =
      thresholdVal !== "" ? parseInt(thresholdVal, 10) : null;

    if (
      !Number.isFinite(parsedLat) ||
      !Number.isFinite(parsedLng) ||
      !Number.isFinite(parsedRadius)
    ) {
      showNotification({
        title: "Validation Error",
        message:
          "Please enter valid numeric values for coordinates and radius.",
        color: "red",
      });
      return;
    }

    updateLocation(
      {
        id: locationId,
        data: {
          latitude: parsedLat,
          longitude: parsedLng,
          radius_allowance: parsedRadius,
          late_arrival_threshold_minutes: parsedThreshold,
        },
      },
      {
        onSuccess: () => {
          showNotification({
            title: "Success",
            message: "Site configuration has been saved successfully.",
            color: "green",
          });
          navigate(locationBackUrl);
        },
        onError: () => {
          showNotification({
            title: "Error",
            message: "Failed to save site configuration. Please try again.",
            color: "red",
          });
        },
      }
    );
  };

  const handleCancel = (): void => {
    navigate(locationBackUrl);
  };

  return (
    <PageContainer>
      <LoadingOverlay visible={isLoading} />

      <PageHeading title="Time & Attendance" />

      <Breadcrumbs
        separator=">"
        classNames={{
          root: "mt-2 mb-6",
          breadcrumb: "hover:no-underline text-sm",
          separator: "text-gray-400",
        }}
      >
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate("/time-attendance")}
        >
          Time &amp; Attendance
        </Text>
        <Text
          className={bcClasses.breadcrumbLink}
          style={{ cursor: "pointer" }}
          onClick={() => navigate(locationBackUrl)}
        >
          {location?.name || "..."}
        </Text>
        <Text className={bcClasses.breadcrumbCurrent}>Site Configuration</Text>
      </Breadcrumbs>

      <Stack spacing="lg">
        {/* Location info header */}
        <Stack spacing={8}>
          <Group spacing="sm" align="center" position="apart">
            <Text weight={500} size="lg">
              {location?.name || "—"}
            </Text>
            {location?.client_name && (
              <ClientBadge clientName={location.client_name} />
            )}
          </Group>
          {location?.region && (
            <Group spacing={4}>
              <MapPin size={16} color="#839099" />
              <Text size="sm" color="dimmed">
                {location.region}
              </Text>
            </Group>
          )}
        </Stack>

        <div className={classes.separator} />

        <Text weight={500} size="lg">
          Site Configuration
        </Text>

        <Tabs active={activeTab} onTabChange={setActiveTab}>
          <Tabs.Tab label="Location">
            <div className="pt-6">
              <Stack spacing="lg" style={{ maxWidth: FORM_MAX_WIDTH }}>
                <Stack spacing={4}>
                  <Text weight={500} size="md">
                    Site Location &amp; Geofence
                  </Text>
                  <Text size="sm" color="dimmed">
                    Set the GPS coordinates and geofence radius for clock-in
                    validation.
                  </Text>
                </Stack>

                {googleMapsApiKey ? (
                  <APIProvider apiKey={googleMapsApiKey}>
                    <Stack spacing="md">
                      <AddressSearch onSelect={handleAddressSelect} />

                      {/* Map */}
                      <div
                        style={{
                          position: "relative",
                          borderRadius: 8,
                          overflow: "hidden",
                        }}
                      >
                        <Map
                          style={{ width: "100%", height: 400 }}
                          defaultCenter={mapCenter}
                          defaultZoom={DEFAULT_ZOOM}
                          gestureHandling="greedy"
                          mapId="site-config-map"
                        >
                          <MapCenterSync
                            lat={lat}
                            lng={lng}
                            enabled={hasCoords}
                          />
                          {hasCoords && (
                            <AdvancedMarker
                              position={{ lat, lng }}
                              draggable
                              onDragEnd={handleMarkerDrag}
                            />
                          )}
                          {hasCoords && radiusMeters > 0 && (
                            <GeofenceCircle
                              center={{ lat, lng }}
                              radius={radiusMeters}
                            />
                          )}
                        </Map>
                        <Button
                          size="xs"
                          variant="outline"
                          color="dark"
                          style={{
                            position: "absolute",
                            bottom: 12,
                            right: 12,
                            backgroundColor: "white",
                            boxShadow: "0 1px 4px rgba(0,0,0,0.15)",
                            zIndex: 1,
                          }}
                          onClick={handleUseCurrentLocation}
                        >
                          Use current
                        </Button>
                      </div>
                    </Stack>
                  </APIProvider>
                ) : null}

                {/* Lat / Lng */}
                <Group grow spacing="sm">
                  <NumberInput
                    label={
                      <Text size="sm" weight={500}>
                        Latitude{" "}
                        <Text component="span" color="red" size="sm">
                          *
                        </Text>
                      </Text>
                    }
                    precision={6}
                    value={
                      form.values.latitude !== ""
                        ? parseFloat(form.values.latitude)
                        : undefined
                    }
                    onChange={(value) =>
                      form.setFieldValue("latitude", String(value ?? ""))
                    }
                    size="sm"
                  />
                  <NumberInput
                    label={
                      <Text size="sm" weight={500}>
                        Longitude{" "}
                        <Text component="span" color="red" size="sm">
                          *
                        </Text>
                      </Text>
                    }
                    precision={6}
                    value={
                      form.values.longitude !== ""
                        ? parseFloat(form.values.longitude)
                        : undefined
                    }
                    onChange={(value) =>
                      form.setFieldValue("longitude", String(value ?? ""))
                    }
                    size="sm"
                  />
                </Group>

                <NumberInput
                  label={
                    <Text size="sm" weight={500}>
                      Geofence Radius (metres){" "}
                      <Text component="span" color="red" size="sm">
                        *
                      </Text>
                    </Text>
                  }
                  value={
                    form.values.radius_allowance !== ""
                      ? parseFloat(form.values.radius_allowance)
                      : undefined
                  }
                  onChange={(value) =>
                    form.setFieldValue("radius_allowance", String(value ?? ""))
                  }
                  min={0}
                  size="sm"
                />
              </Stack>
            </div>
          </Tabs.Tab>

          <Tabs.Tab label="Shift rules">
            <div className="pt-6">
              <Stack spacing="lg" style={{ maxWidth: FORM_MAX_WIDTH }}>
                <Stack spacing={4}>
                  <Text weight={500} size="md">
                    Late Arrival Threshold
                  </Text>
                  <Text size="sm" color="dimmed">
                    Set the minutes before employee is marked as a late arrival
                  </Text>
                </Stack>

                <Select
                  label={
                    <Text size="sm" weight={500}>
                      Threshold (minutes){" "}
                      <Text component="span" color="red" size="sm">
                        *
                      </Text>
                    </Text>
                  }
                  placeholder="Select threshold"
                  data={LATE_ARRIVAL_OPTIONS}
                  value={form.values.late_arrival_threshold_minutes || null}
                  onChange={(value) =>
                    form.setFieldValue(
                      "late_arrival_threshold_minutes",
                      value ?? ""
                    )
                  }
                  size="sm"
                  style={{ maxWidth: 300 }}
                />
              </Stack>
            </div>
          </Tabs.Tab>

          <Tabs.Tab label="Site & Grouping">
            <ShiftSiteGroupingTab locationId={locationId || ""} />
          </Tabs.Tab>

          <Tabs.Tab label="Spot Checks" disabled>
            <div className="pt-6" />
          </Tabs.Tab>
        </Tabs>

        <div className={classes.separator} />

        <Group position="right" spacing="sm">
          <Button
            variant="outline"
            color="dark"
            onClick={handleCancel}
            disabled={isSubmitting}
            style={{ minWidth: 118 }}
          >
            Cancel
          </Button>
          <Button
            color="dark"
            onClick={handleSave}
            loading={isSubmitting}
            style={{ minWidth: 118 }}
          >
            Save Changes
          </Button>
        </Group>
      </Stack>
    </PageContainer>
  );
};

const ShiftSiteConfigurationPage: React.FC = () => (
  <AccessGuard>
    <ShiftSiteConfigurationPageInner />
  </AccessGuard>
);

export default ShiftSiteConfigurationPage;
